import { useAtomValue } from "jotai";
import { Spinner } from "@common/index";
import { currentUserAtom, userFetchErrorAtom } from "@/store/userAtoms";
import { RoleType } from "@models/user";
import { NotFoundPage } from "@customer/ErrorPages/NotFoundPage";
import { AccountLoadError } from "./AccountLoadError";

// Client-side role gate for the admin tree (#401). Sits INSIDE ProtectedRoute,
// which owns the login redirect and the getMeUser() call that fills
// currentUserAtom; this only decides what an authenticated user may see.
// A non-admin gets the customer-branded 404 — the same page any unknown URL
// shows — so nothing here hints that an admin surface exists.
export const AdminRoute = ({ children }: AdminRouteProps) => {
    const user = useAtomValue(currentUserAtom);
    const hasFetchError = useAtomValue(userFetchErrorAtom);

    if (!user) {
        // Failed is distinct from loading: a read that errored would
        // otherwise leave the spinner up forever.
        if (hasFetchError) {
            return <AccountLoadError />;
        }
        // Loading window, same as ApprovalGate: no flash of either outcome.
        return (
            <div className="flex justify-center py-20">
                <Spinner size="lg" />
            </div>
        );
    }

    // The field ships (#401); the optional chain is deploy-skew resilience —
    // an older API without it must read as "not admin", never crash the gate.
    const isAdmin = user.permissions?.includes(RoleType.Admin) ?? false;
    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-base-200">
                <NotFoundPage />
            </div>
        );
    }

    return <>{children}</>;
};

interface AdminRouteProps {
    children: React.ReactNode;
}
